import { Component, Inject, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import moment from "moment";

type CalendarType = "YYYY" | "MM" | "DD" | "YYYY-MM" | "YYYY-MM-DD" | "MM-DD";
/* ---------------- USAGE EXAMPLE ----------------

this.openCalender();
openCalender() {
        this.subscriptions.push(
            this.dialog
                .open(CalendarComponent, {
                    width: this.matDialogSizeService.wIII,
                    disableClose: true,
                    data: {
                        type: "YYYY-MM-DD",
                        validateRange: {
                            minDate: moment("2020-01-01").format("YYYY-MM-DD"),
                            maxDate: moment("2024-01-01").format("YYYY-MM-DD"),
                        },
                        title: "Test Calendar Dialog",
                    },
                })
                .afterClosed()
                .pipe(
                    map((res: any) => {
                        if (res) {
                            console.log("CALENDAR DIALOG CLOSED DATA:", res);
                        }
                    })
                )
                .subscribe()
        );
    }
*/

@Component({
    selector: "web-calendar",
    templateUrl: "./calendar.component.html",
    styleUrls: ["./calendar.component.scss"],
})
export class CalendarComponent implements OnInit {
    content: any;
    rows: number[][] = [];

    selectedYearValue?: number;
    selectedMonthValue?: number;
    selectedDayValue?: number;

    currentYear = moment().year();
    currentStartYear = this.currentYear;

    selectedType: "YYYY" | "MM" | "DD" = "DD";
    moment = moment;

    constructor(
        public dialog: MatDialog,
        public dialogRef: MatDialogRef<CalendarComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any
    ) {
        this.content = { ...data };

        if (this.content?.selectedDate) {
            this.selectedDate(this.content?.selectedDate, this.content?.type);
        }

        this.initialize();
    }

    ngOnInit(): void {}

    selectedDate(value: string, type: string): void {
        // reset
        this.selectedYearValue = undefined;
        this.selectedMonthValue = undefined;
        this.selectedDayValue = undefined;

        if (!value || !type) return;

        const formats = ["YYYY", "YYYY-MM", "YYYY-MM-DD", "MM", "MM-DD", "DD"];

        const m = moment(value, formats, true); // strict parsing
        if (!m.isValid()) return;

        this.selectedYearValue = parseInt("" + m.year());
        this.selectedMonthValue = parseInt("" + (m.month() + 1)); // moment month is 0-based
        this.selectedDayValue = parseInt("" + m.date());

        // if (type.includes("YYYY")) {
        //     this.selectedYearValue = parseInt("" + m.year());
        // }

        // if (type.includes("MM")) {
        //     this.selectedMonthValue = parseInt("" + (m.month() + 1)); // moment month is 0-based
        // }

        // if (type.includes("DD")) {
        //     this.selectedDayValue = parseInt("" + m.date());
        // }
    }

    /* ---------------- INIT ---------------- */

    private initialize(): void {
        const type: CalendarType = this.content?.type;

        if (type === "YYYY") {
            this.selectedType = "YYYY";
            this.generateYearRows(true);
            return;
        }

        if (type === "MM" || type === "YYYY-MM") {
            if (type === "MM") {
                this.selectedType = "MM";
                this.monthRowsCount();
                return;
            }
            this.selectedType = "YYYY";
            this.generateYearRows(true);
            return;
        }

        // Day selection
        if (type === "YYYY-MM-DD" || type === "MM-DD") {
            this.generateYearRows(true);
        }

        this.selectedDayValue = this.content?.selectedDate
            ? moment(this.content.selectedDate, "DD").date()
            : moment().date();
        this.selectedType = "DD";
        this.daysRowsCount();
    }

    /* ---------------- ROW GENERATORS ---------------- */

    daysRowsCount(): void {
        this.rows = [];
        const year = this.selectedYearValue;
        const month = this.selectedMonthValue;

        let dayLength = 31; // default max days
        if (year && month) {
            dayLength = moment(`${year}-${month}`, "YYYY-MM").daysInMonth();
        }

        this.rows = this.chunkArray(
            Array.from({ length: dayLength }, (_, i) => i + 1),
            6
        );
    }

    monthRowsCount(): void {
        // debugger;
        // this.selectedYearValue = this.content?.selectedDate
        //     ? moment(this.content.selectedDate, "YYYY").year()
        //     : undefined;

        this.rows = this.chunkArray(
            Array.from({ length: 12 }, (_, i) => i + 1),
            4
        );
    }

    generateYearRows(reset = false): void {
        const type = this.content?.type;
        const range = this.content?.validateRange;

        const baseDate = range?.maxDate || range?.minDate || moment();
        const baseMoment = moment(baseDate);

        // ✅ Reset ONLY on first load or type change
        if (reset) {
            this.currentStartYear = this.selectedYearValue ? this.selectedYearValue : baseMoment.year() - 15;

            this.selectedYearValue = this.selectedYearValue ? this.selectedYearValue : baseMoment.year();

            if (type === "YYYY-MM" || type === "YYYY-MM-DD" || type === "MM-DD") {
                this.selectedMonthValue = baseMoment.month() + 1;
            } else {
                this.selectedMonthValue = undefined;
            }

            if (type === "YYYY-MM-DD" || type === "MM-DD") {
                this.selectedDayValue = baseMoment.date();
            } else {
                this.selectedDayValue = undefined;
            }
        }

        // Build year grid
        this.rows = this.chunkArray(
            Array.from({ length: 16 }, (_, i) => this.currentStartYear + i),
            4
        );
    }

    /* ---------------- NAVIGATION ---------------- */

    nextYears(): void {
        this.currentStartYear += 16;
        this.generateYearRows();
    }

    previousYears(): void {
        this.currentStartYear -= 16;
        this.generateYearRows();
    }

    changeValue(type: "YYYY" | "MM" | "DD"): void {
        this.selectedType = type;
        if (type === "YYYY") this.generateYearRows();
        if (type === "MM") this.monthRowsCount();
        if (type === "DD") this.daysRowsCount();
    }

    select(value: number, type: "YYYY" | "MM" | "DD"): void {
        if (type === "YYYY") this.selectedYearValue = value;
        if (type === "MM") this.selectedMonthValue = value;
        if (type === "DD") this.selectedDayValue = value;
    }

    /* ---------------- DISABLE LOGIC ---------------- */

    disabledYear(year: number): boolean {
        return this.isOutOfRange(moment(year, "YYYY"), "year");
    }

    disabledMonth(month: number): boolean {
        const paddedMonth = ("0" + month).slice(-2);
        if (!this.selectedYearValue) return false;
        return this.isOutOfRange(moment(`${this.selectedYearValue}-${paddedMonth}`, "YYYY-MM"), "month");
    }

    disabledDay(day: number): boolean {
        if (!this.selectedYearValue || !this.selectedMonthValue) return false;
        return this.isOutOfRange(
            moment(`${this.selectedYearValue}-${this.selectedMonthValue}-${day}`, "YYYY-MM-DD"),
            "day"
        );
    }

    private isOutOfRange(date: moment.Moment, unit: moment.unitOfTime.StartOf): boolean {
        const { minDate, maxDate } = this.content?.validateRange || {};
        if (minDate && date.isBefore(moment(minDate), unit)) return true;
        if (maxDate && date.isAfter(moment(maxDate), unit)) return true;
        return false;
    }

    /* ---------------- CONFIRM / CANCEL ---------------- */

    onConfirmClick(): void {
        const type = this.content.type;
        // debugger;
        if (type === "YYYY-MM" && this.selectedType === "YYYY") {
            this.changeValue("MM");
            this.selectedMonthValue = undefined;
            return;
        }

        if (type === "YYYY-MM-DD") {
            if (this.selectedType === "YYYY") {
                this.changeValue("MM");
                this.selectedMonthValue = undefined;
                return;
            }
            if (this.selectedType === "MM") {
                this.changeValue("DD");
                this.selectedDayValue = undefined;
                return;
            }
        }

        const value = [
            this.selectedYearValue,
            this.selectedMonthValue && ("0" + this.selectedMonthValue).slice(-2),
            this.selectedDayValue && ("0" + this.selectedDayValue).slice(-2),
        ]
            .filter(Boolean)
            .join("-");

        this.dialogRef.close({ success: true, value });
    }

    onCancelClick(): void {
        this.dialogRef.close({ success: false });
    }

    /* ---------------- UTILS ---------------- */

    private chunkArray<T>(array: T[], size: number): T[][] {
        const result: T[][] = [];
        for (let i = 0; i < array.length; i += size) {
            result.push(array.slice(i, i + size));
        }
        return result;
    }

    trackByIndex(index: number): number {
        return index;
    }
}
